import { apiUrl } from './api-client'
import { AUTH_ERROR_CODES } from './auth-client'
import type { RequestOptions } from './api-client'
import type { AuthErrorCode, AuthFailure } from './auth-client'

/**
 * Getypeerde toegang tot de `/residences/:id/media`-endpoints van de platform-API.
 *
 * Drie dingen liggen hier vast, in lijn met `api-client.ts` en `auth-client.ts`:
 *
 * 1. **Eén foutafhandeling.** Een aanroep werpt nooit; alles komt terug als een
 *    `MediaFailure` met een `kind` en — wanneer de API een herkenbare
 *    `{ error: { code, message } }` teruggaf — de foutcode uit het contract. Die
 *    codelijst is contractbreed en wordt hier niet opnieuw verzonnen maar uit
 *    `auth-client.ts` overgenomen; `MediaFailure` is letterlijk `AuthFailure`.
 * 2. **De sessiecookie moet mee.** Zelfde reden als bij `auth-client.ts`:
 *    `siteshowroom_session` is httpOnly en gaat alleen mee met
 *    `credentials: 'include'`.
 * 3. **De API blijft de autoriteit.** De controles in `inspectUpload` besparen
 *    een kansloze upload van 25 MB; ze vervángen de servercontrole niet. De API
 *    kijkt naar de bytes, de browser alleen naar wat het bestandsobject zegt
 *    (CLAUDE.md regel 2: geen bedrijfslogica die alleen in de frontend bestaat).
 *
 * `exchange`, `httpFailure` en de kleine hulpfuncties staan letterlijk zo in
 * `auth-client.ts`, maar zijn daar niet geëxporteerd en kennen alleen GET en
 * POST met een JSON-body. Media heeft PATCH, PUT, DELETE en multipart nodig.
 * Verhuist dit ooit naar een gedeelde module, dan verdwijnt deze kopie mee.
 */

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

/** Foutcodes zijn contractbreed; media kent er geen eigen. */
export type MediaErrorCode = AuthErrorCode

/** `ApiFailure` plus de foutcode van het contract — zie `AuthFailure`. */
export type MediaFailure = AuthFailure

export type MediaResult<T> =
  | { readonly ok: true; readonly data: T; readonly roundTripMs: number }
  | { readonly ok: false; readonly error: MediaFailure }

/** Statussen uit `media_assets.status` (migratie 0009). */
export const MEDIA_STATUSES = ['processing', 'ready', 'failed'] as const
export type MediaStatus = (typeof MEDIA_STATUSES)[number]

/**
 * Categorieën uit de CHECK-constraint op `media_assets.category`. De volgorde is
 * die van de keuzelijst: van buiten naar binnen, plattegrond en overig als
 * sluitstuk.
 */
export const MEDIA_CATEGORIES = [
  'exterior',
  'living',
  'kitchen',
  'bedroom',
  'bathroom',
  'outdoor',
  'surroundings',
  'floorplan',
  'other',
] as const
export type MediaCategory = (typeof MEDIA_CATEGORIES)[number]

/** Alt-tekst en bijschrift van één taal. Beide zijn data, geen presentatie (§17.14). */
export interface MediaText {
  readonly altText: string
  readonly caption: string
}

export interface MediaUrls {
  readonly original: string
  readonly thumb: string
  readonly card: string
  readonly hero: string
  readonly og: string
}

export interface MediaAsset {
  readonly id: string
  readonly residenceId: string
  readonly status: MediaStatus
  readonly mimeType: string
  readonly byteSize: number
  readonly width: number
  readonly height: number
  readonly blurhash: string
  readonly dominantColor: string
  readonly category: MediaCategory
  /** De ruimte waar deze foto bij hoort; `null` wanneer hij nog niet is ingedeeld. */
  readonly spaceId: string | null
  readonly sortOrder: number
  readonly isPrimary: boolean
  readonly originalFilename: string
  readonly urls: MediaUrls
  /** Sleutel is de taalcode; talen zonder tekst ontbreken. */
  readonly texts: Readonly<Record<string, MediaText>>
  readonly createdAt: string
}

/**
 * Wat het mediascherm van een residence nodig heeft: kiezen en benoemen, meer
 * niet. Zodra er een volwaardige residences-client bestaat verhuist dit daarheen
 * en wordt dit een re-export — niet een tweede definitie.
 */
export interface ResidenceOption {
  readonly id: string
  readonly name: string
  readonly slug: string
}

// ---------------------------------------------------------------------------
// Grenzen van de ingest (contract stage 2)
// ---------------------------------------------------------------------------

/** 25 MB. */
export const MEDIA_MAX_BYTES = 25 * 1024 * 1024

/**
 * Kanalen hanteren eigen minimumafmetingen en uit een kleine foto is geen grote
 * te maken (§17.14) — daarom een ondergrens en geen automatische opschaling.
 */
export const MEDIA_MIN_WIDTH = 1600

/** Op de bytes gecontroleerd door de API; hier alleen om de kiezer te filteren. */
export const MEDIA_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'] as const

/** Waarde voor het `accept`-attribuut van de bestandskiezer. */
export const MEDIA_ACCEPT_ATTRIBUTE = MEDIA_ACCEPTED_TYPES.join(',')

/** Reden waarom een bestand de upload niet in hoeft. */
export type UploadRejection = 'type' | 'tooLarge' | 'tooSmall'

// ---------------------------------------------------------------------------
// Hulpfuncties (kopie uit auth-client.ts; zie de kop van dit bestand)
// ---------------------------------------------------------------------------

function isAbortError(cause: unknown): boolean {
  return cause instanceof Error && cause.name === 'AbortError'
}

function describeCause(cause: unknown): string {
  if (cause instanceof Error) {
    return cause.message
  }
  return String(cause)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isMediaErrorCode(value: string): value is MediaErrorCode {
  const known: readonly string[] = AUTH_ERROR_CODES
  return known.includes(value)
}

function isSuccessStatus(status: number): boolean {
  return status >= 200 && status < 300
}

/** Leest `{ error: { code, message } }` uit een foutantwoord; null als die vorm ontbreekt. */
function readErrorBody(payload: unknown): { code: MediaErrorCode; message: string } | null {
  if (!isRecord(payload)) {
    return null
  }
  const error = payload['error']
  if (!isRecord(error)) {
    return null
  }
  const code = error['code']
  if (typeof code !== 'string' || !isMediaErrorCode(code)) {
    return null
  }
  const message = error['message']
  return { code, message: typeof message === 'string' ? message : code }
}

/** Zet een niet-2xx-antwoord om naar een `MediaFailure`, mét foutcode wanneer die er is. */
function httpFailure(
  path: string,
  status: number,
  statusText: string,
  payload: unknown,
): MediaFailure {
  const suffix = statusText === '' ? '' : ` ${statusText}`
  const base = `${path}: HTTP ${String(status)}${suffix}`
  const body = readErrorBody(payload)
  if (body === null) {
    return { kind: 'http', status, message: base }
  }
  return { kind: 'http', status, code: body.code, message: `${base} — ${body.message}` }
}

function parseBody(raw: string): unknown {
  if (raw.trim() === '') {
    return null
  }
  try {
    return JSON.parse(raw)
  } catch {
    // De aanroeper ziet dit terug als een ontbrekende vorm; bij een foutstatus
    // is een onleesbare body te overleven.
    return undefined
  }
}

// ---------------------------------------------------------------------------
// Uitwisseling met de API
// ---------------------------------------------------------------------------

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

interface Exchange {
  readonly status: number
  readonly statusText: string
  readonly payload: unknown
  readonly roundTripMs: number
}

type ExchangeResult =
  | { readonly ok: true; readonly exchange: Exchange }
  | { readonly ok: false; readonly error: MediaFailure }

async function exchange(
  method: Method,
  path: string,
  body: unknown,
  options: RequestOptions,
): Promise<ExchangeResult> {
  const startedAt = performance.now()

  let response: Response
  try {
    response = await fetch(apiUrl(path), {
      method,
      credentials: 'include',
      headers:
        body === undefined
          ? { accept: 'application/json' }
          : { accept: 'application/json', 'content-type': 'application/json' },
      body: body === undefined ? null : JSON.stringify(body),
      signal: options.signal ?? null,
    })
  } catch (cause: unknown) {
    if (isAbortError(cause)) {
      return { ok: false, error: { kind: 'aborted', message: `${path}: request aborted` } }
    }
    return { ok: false, error: { kind: 'network', message: `${path}: ${describeCause(cause)}` } }
  }

  // 204 heeft per definitie geen body; `response.text()` zou een lege string
  // geven, maar het onderscheid expliciet maken leest beter.
  if (response.status === 204) {
    return {
      ok: true,
      exchange: {
        status: 204,
        statusText: response.statusText,
        payload: null,
        roundTripMs: performance.now() - startedAt,
      },
    }
  }

  let raw: string
  try {
    raw = await response.text()
  } catch (cause: unknown) {
    if (isAbortError(cause)) {
      return { ok: false, error: { kind: 'aborted', message: `${path}: request aborted` } }
    }
    return { ok: false, error: { kind: 'network', message: `${path}: ${describeCause(cause)}` } }
  }

  const payload = parseBody(raw)
  if (payload === undefined && response.ok) {
    return {
      ok: false,
      error: { kind: 'invalid-response', message: `${path}: invalid JSON` },
    }
  }

  return {
    ok: true,
    exchange: {
      status: response.status,
      statusText: response.statusText,
      payload: payload ?? null,
      roundTripMs: performance.now() - startedAt,
    },
  }
}

// ---------------------------------------------------------------------------
// Validatie van de antwoordvorm
// ---------------------------------------------------------------------------
//
// Zelfde vorm als `describeHealthProblem` in `api-client.ts`: een omschrijving
// van het eerste probleem, of null. Zo wordt een afwijkend API-antwoord
// zichtbaar in plaats van als `undefined` op het scherm te belanden.

function isMediaStatus(value: unknown): value is MediaStatus {
  const known: readonly string[] = MEDIA_STATUSES
  return typeof value === 'string' && known.includes(value)
}

function isMediaCategory(value: unknown): value is MediaCategory {
  const known: readonly string[] = MEDIA_CATEGORIES
  return typeof value === 'string' && known.includes(value)
}

function describeUrlsProblem(value: unknown): string | null {
  if (!isRecord(value)) {
    return 'missing "urls"'
  }
  for (const name of ['original', 'thumb', 'card', 'hero', 'og'] as const) {
    if (typeof value[name] !== 'string') {
      return `missing "urls.${name}"`
    }
  }
  return null
}

function describeAssetProblem(value: unknown): string | null {
  if (!isRecord(value)) {
    return 'asset is not an object'
  }
  for (const field of [
    'id',
    'residenceId',
    'mimeType',
    'blurhash',
    'dominantColor',
    'originalFilename',
    'createdAt',
  ] as const) {
    if (typeof value[field] !== 'string') {
      return `missing "asset.${field}"`
    }
  }
  for (const field of ['byteSize', 'width', 'height', 'sortOrder'] as const) {
    if (typeof value[field] !== 'number') {
      return `missing "asset.${field}"`
    }
  }
  if (typeof value['isPrimary'] !== 'boolean') {
    return 'missing "asset.isPrimary"'
  }
  if (!isMediaStatus(value['status'])) {
    return '"asset.status" is not a known status'
  }
  // De database kent een CHECK-constraint op deze lijst; een andere waarde is
  // een contractbreuk, geen datavariatie, en hoort dus zichtbaar te falen.
  if (!isMediaCategory(value['category'])) {
    return '"asset.category" is not a known category'
  }
  const urlsProblem = describeUrlsProblem(value['urls'])
  if (urlsProblem !== null) {
    return urlsProblem
  }
  if (value['texts'] !== undefined && !isRecord(value['texts'])) {
    return '"asset.texts" must be an object'
  }
  return null
}

/** Leest één taalveld en valt terug op een lege string; een half record is geen fout. */
function toText(value: unknown): MediaText {
  if (!isRecord(value)) {
    return { altText: '', caption: '' }
  }
  const altText = value['altText']
  const caption = value['caption']
  return {
    altText: typeof altText === 'string' ? altText : '',
    caption: typeof caption === 'string' ? caption : '',
  }
}

function toTexts(value: unknown): Readonly<Record<string, MediaText>> {
  if (!isRecord(value)) {
    return {}
  }
  const texts: Record<string, MediaText> = {}
  for (const [locale, entry] of Object.entries(value)) {
    texts[locale] = toText(entry)
  }
  return texts
}

function toAsset(value: Record<string, unknown>): MediaAsset {
  // De vorm is hierboven runtime-gecontroleerd; de casts maken dat expliciet.
  const urls = value['urls'] as Record<string, unknown>
  return {
    id: value['id'] as string,
    residenceId: value['residenceId'] as string,
    status: value['status'] as MediaStatus,
    mimeType: value['mimeType'] as string,
    byteSize: value['byteSize'] as number,
    width: value['width'] as number,
    height: value['height'] as number,
    blurhash: value['blurhash'] as string,
    dominantColor: value['dominantColor'] as string,
    category: value['category'] as MediaCategory,
    spaceId: typeof value['spaceId'] === 'string' ? value['spaceId'] : null,
    sortOrder: value['sortOrder'] as number,
    isPrimary: value['isPrimary'] as boolean,
    originalFilename: value['originalFilename'] as string,
    urls: {
      original: urls['original'] as string,
      thumb: urls['thumb'] as string,
      card: urls['card'] as string,
      hero: urls['hero'] as string,
      og: urls['og'] as string,
    },
    texts: toTexts(value['texts']),
    createdAt: value['createdAt'] as string,
  }
}

type AssetEnvelope =
  | { readonly ok: true; readonly asset: MediaAsset }
  | { readonly ok: false; readonly error: MediaFailure }

function readAssetEnvelope(path: string, payload: unknown): AssetEnvelope {
  if (!isRecord(payload)) {
    return {
      ok: false,
      error: { kind: 'invalid-response', message: `${path}: body is not an object` },
    }
  }
  const problem = describeAssetProblem(payload['asset'])
  if (problem !== null) {
    return { ok: false, error: { kind: 'invalid-response', message: `${path}: ${problem}` } }
  }
  return { ok: true, asset: toAsset(payload['asset'] as Record<string, unknown>) }
}

type AssetListEnvelope =
  | { readonly ok: true; readonly assets: readonly MediaAsset[] }
  | { readonly ok: false; readonly error: MediaFailure }

function readAssetListEnvelope(path: string, payload: unknown): AssetListEnvelope {
  if (!isRecord(payload)) {
    return {
      ok: false,
      error: { kind: 'invalid-response', message: `${path}: body is not an object` },
    }
  }
  const list = payload['assets']
  if (!Array.isArray(list)) {
    return { ok: false, error: { kind: 'invalid-response', message: `${path}: missing "assets"` } }
  }
  const assets: MediaAsset[] = []
  for (const entry of list) {
    const problem = describeAssetProblem(entry)
    if (problem !== null) {
      return { ok: false, error: { kind: 'invalid-response', message: `${path}: ${problem}` } }
    }
    assets.push(toAsset(entry as Record<string, unknown>))
  }
  return { ok: true, assets }
}

// ---------------------------------------------------------------------------
// URL's van de bestanden
// ---------------------------------------------------------------------------

/**
 * Maakt van de URL in het antwoord een adres dat de browser kan ophalen.
 *
 * De API geeft een pad terug (`/media/{tenant}/{residence}/{asset}/{file}`), niet
 * een volledige URL: waar de dienst draait weet de client, niet de server. Tijdens
 * `pnpm dev` loopt dat pad via dezelfde proxy als de rest van de API, dus gaat het
 * door `apiUrl`. Een antwoord dat wél een absolute URL geeft — straks een
 * CDN-domein bij de s3-driver — blijft ongemoeid.
 */
export function mediaFileUrl(url: string): string {
  if (url.startsWith('//') || /^https?:\/\//i.test(url)) {
    return url
  }
  return apiUrl(url)
}

// ---------------------------------------------------------------------------
// Controle vóór de upload
// ---------------------------------------------------------------------------

/**
 * Kijkt of het bestand kansloos is voordat het het netwerk op gaat.
 *
 * Nadrukkelijk géén vervanging van de servercontrole: de API kijkt naar de
 * bytes, de browser alleen naar wat het bestandsobject beweert. Wat dit wél
 * bespaart is een upload van tientallen megabytes die gegarandeerd geweigerd
 * wordt, en een gebruiker die pas na een minuut wachten hoort dat zijn foto te
 * klein is.
 *
 * Lukt het decoderen niet, dan is dat hier géén afwijzing: niet elke browser
 * decodeert avif, en de API velt het oordeel op de bytes.
 */
export async function inspectUpload(file: File): Promise<UploadRejection | null> {
  const accepted: readonly string[] = MEDIA_ACCEPTED_TYPES
  // Een leeg `type` komt voor bij bestanden zonder herkenbare extensie; dan
  // beslist de server.
  if (file.type !== '' && !accepted.includes(file.type)) {
    return 'type'
  }
  if (file.size > MEDIA_MAX_BYTES) {
    return 'tooLarge'
  }

  let bitmap: ImageBitmap
  try {
    bitmap = await createImageBitmap(file)
  } catch {
    return null
  }
  const width = bitmap.width
  // Vrijgeven, anders blijft de gedecodeerde bitmap in het geheugen staan.
  bitmap.close()
  return width < MEDIA_MIN_WIDTH ? 'tooSmall' : null
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

function mediaPath(residenceId: string): string {
  return `/residences/${encodeURIComponent(residenceId)}/media`
}

function assetPath(residenceId: string, assetId: string): string {
  return `${mediaPath(residenceId)}/${encodeURIComponent(assetId)}`
}

/** `GET /residences` — alleen wat het mediascherm nodig heeft om te kiezen. */
export async function listResidences(
  options: RequestOptions = {},
): Promise<MediaResult<readonly ResidenceOption[]>> {
  const path = '/residences'
  const result = await exchange('GET', path, undefined, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex.status, ex.statusText, ex.payload) }
  }
  if (!isRecord(ex.payload) || !Array.isArray(ex.payload['residences'])) {
    return {
      ok: false,
      error: { kind: 'invalid-response', message: `${path}: missing "residences"` },
    }
  }
  const residences: ResidenceOption[] = []
  for (const entry of ex.payload['residences']) {
    if (!isRecord(entry) || typeof entry['id'] !== 'string' || typeof entry['name'] !== 'string') {
      return {
        ok: false,
        error: { kind: 'invalid-response', message: `${path}: missing "residence.id" or "name"` },
      }
    }
    const slug = entry['slug']
    residences.push({
      id: entry['id'],
      name: entry['name'],
      slug: typeof slug === 'string' ? slug : '',
    })
  }
  return { ok: true, data: residences, roundTripMs: ex.roundTripMs }
}

/** `GET /residences/:id/media` */
export async function listMedia(
  residenceId: string,
  options: RequestOptions = {},
): Promise<MediaResult<readonly MediaAsset[]>> {
  const path = mediaPath(residenceId)
  const result = await exchange('GET', path, undefined, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex.status, ex.statusText, ex.payload) }
  }
  const envelope = readAssetListEnvelope(path, ex.payload)
  if (!envelope.ok) {
    return { ok: false, error: envelope.error }
  }
  return { ok: true, data: envelope.assets, roundTripMs: ex.roundTripMs }
}

export interface UploadOptions {
  /** Voortgang tussen 0 en 1; alleen aangeroepen zolang de browser hem kent. */
  readonly onProgress?: ((fraction: number) => void) | undefined
  readonly signal?: AbortSignal | undefined
}

/**
 * `POST /residences/:id/media` — multipart, veld `file`.
 *
 * Als enige aanroep in dit bestand op XMLHttpRequest en niet op `fetch`. Reden:
 * `fetch` kent geen voortgang van een *upload*. Een streamende request body is
 * niet in alle browsers beschikbaar en vereist HTTP/2 zonder Content-Length,
 * terwijl `xhr.upload.onprogress` overal werkt. Voor een scherm waarop iemand
 * twintig foto's van 20 MB tegelijk aanbiedt is die voortgangsbalk geen luxe.
 */
export function uploadMedia(
  residenceId: string,
  file: File,
  options: UploadOptions = {},
): Promise<MediaResult<MediaAsset>> {
  const path = mediaPath(residenceId)
  const startedAt = performance.now()

  return new Promise<MediaResult<MediaAsset>>((resolve) => {
    const form = new FormData()
    // De veldnaam ligt vast in het contract; de bestandsnaam gaat mee ter
    // herkenning en wordt door de API nooit als opslagsleutel gebruikt (§17.2).
    form.append('file', file, file.name)

    const request = new XMLHttpRequest()
    request.open('POST', apiUrl(path), true)
    request.responseType = 'text'
    request.withCredentials = true
    request.setRequestHeader('accept', 'application/json')

    // Één keer beantwoorden: `abort` vuurt zowel `onabort` als `onloadend`.
    let settled = false
    function settle(result: MediaResult<MediaAsset>): void {
      if (settled) {
        return
      }
      settled = true
      options.signal?.removeEventListener('abort', handleAbort)
      resolve(result)
    }

    function handleAbort(): void {
      request.abort()
    }

    if (options.onProgress !== undefined) {
      const onProgress = options.onProgress
      request.upload.onprogress = (event: ProgressEvent): void => {
        if (event.lengthComputable && event.total > 0) {
          onProgress(Math.min(1, event.loaded / event.total))
        }
      }
    }

    request.onerror = (): void => {
      settle({ ok: false, error: { kind: 'network', message: `${path}: request failed` } })
    }
    request.ontimeout = (): void => {
      settle({ ok: false, error: { kind: 'network', message: `${path}: request timed out` } })
    }
    request.onabort = (): void => {
      settle({ ok: false, error: { kind: 'aborted', message: `${path}: request aborted` } })
    }
    request.onload = (): void => {
      const payload = parseBody(request.responseText)
      if (!isSuccessStatus(request.status)) {
        settle({
          ok: false,
          error: httpFailure(path, request.status, request.statusText, payload ?? null),
        })
        return
      }
      if (payload === undefined) {
        settle({ ok: false, error: { kind: 'invalid-response', message: `${path}: invalid JSON` } })
        return
      }
      const envelope = readAssetEnvelope(path, payload)
      if (!envelope.ok) {
        settle({ ok: false, error: envelope.error })
        return
      }
      settle({ ok: true, data: envelope.asset, roundTripMs: performance.now() - startedAt })
    }

    if (options.signal !== undefined) {
      if (options.signal.aborted) {
        settle({ ok: false, error: { kind: 'aborted', message: `${path}: request aborted` } })
        return
      }
      options.signal.addEventListener('abort', handleAbort)
    }

    request.send(form)
  })
}

export interface MediaAssetPatch {
  readonly category?: MediaCategory | undefined
  readonly sortOrder?: number | undefined
}

/** `PATCH /residences/:id/media/:assetId` */
export async function updateMedia(
  residenceId: string,
  assetId: string,
  patch: MediaAssetPatch,
  options: RequestOptions = {},
): Promise<MediaResult<MediaAsset>> {
  const path = assetPath(residenceId, assetId)
  // Alleen meesturen wat werkelijk verandert: een PATCH die alle velden herhaalt
  // overschrijft stilletjes wat iemand anders intussen heeft gewijzigd.
  const body: Record<string, unknown> = {}
  if (patch.category !== undefined) {
    body['category'] = patch.category
  }
  if (patch.sortOrder !== undefined) {
    body['sortOrder'] = patch.sortOrder
  }
  const result = await exchange('PATCH', path, body, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex.status, ex.statusText, ex.payload) }
  }
  const envelope = readAssetEnvelope(path, ex.payload)
  if (!envelope.ok) {
    return { ok: false, error: envelope.error }
  }
  return { ok: true, data: envelope.asset, roundTripMs: ex.roundTripMs }
}

/** `PUT /residences/:id/media/:assetId/primary` */
export async function setPrimaryMedia(
  residenceId: string,
  assetId: string,
  options: RequestOptions = {},
): Promise<MediaResult<MediaAsset>> {
  const path = `${assetPath(residenceId, assetId)}/primary`
  const result = await exchange('PUT', path, undefined, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex.status, ex.statusText, ex.payload) }
  }
  const envelope = readAssetEnvelope(path, ex.payload)
  if (!envelope.ok) {
    return { ok: false, error: envelope.error }
  }
  return { ok: true, data: envelope.asset, roundTripMs: ex.roundTripMs }
}

/** `PUT /residences/:id/media/:assetId/texts/:locale` */
export async function saveMediaText(
  residenceId: string,
  assetId: string,
  locale: string,
  text: MediaText,
  options: RequestOptions = {},
): Promise<MediaResult<MediaText>> {
  const path = `${assetPath(residenceId, assetId)}/texts/${encodeURIComponent(locale)}`
  const result = await exchange(
    'PUT',
    path,
    { altText: text.altText, caption: text.caption },
    options,
  )
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex.status, ex.statusText, ex.payload) }
  }
  if (!isRecord(ex.payload) || !isRecord(ex.payload['text'])) {
    return { ok: false, error: { kind: 'invalid-response', message: `${path}: missing "text"` } }
  }
  return { ok: true, data: toText(ex.payload['text']), roundTripMs: ex.roundTripMs }
}

/**
 * `PUT /residences/:id/media/order` — de volledige volgorde in één aanroep.
 *
 * Bewust niet één PATCH per foto: bij vijftien foto's zijn dat vijftien
 * aanvragen waarvan er één kan mislukken, met een halve volgorde tot gevolg.
 */
export async function reorderMedia(
  residenceId: string,
  assetIds: readonly string[],
  options: RequestOptions = {},
): Promise<MediaResult<readonly MediaAsset[]>> {
  const path = `${mediaPath(residenceId)}/order`
  const result = await exchange('PUT', path, { assetIds }, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  if (!isSuccessStatus(ex.status)) {
    return { ok: false, error: httpFailure(path, ex.status, ex.statusText, ex.payload) }
  }
  const envelope = readAssetListEnvelope(path, ex.payload)
  if (!envelope.ok) {
    return { ok: false, error: envelope.error }
  }
  return { ok: true, data: envelope.assets, roundTripMs: ex.roundTripMs }
}

/** `DELETE /residences/:id/media/:assetId` — 204 zonder body. */
export async function deleteMedia(
  residenceId: string,
  assetId: string,
  options: RequestOptions = {},
): Promise<MediaResult<null>> {
  const path = assetPath(residenceId, assetId)
  const result = await exchange('DELETE', path, undefined, options)
  if (!result.ok) {
    return result
  }
  const ex = result.exchange
  // Een 404 telt als geslaagd: de foto is weg, en dat is precies wat de
  // aanroeper wilde bereiken.
  if (!isSuccessStatus(ex.status) && ex.status !== 404) {
    return { ok: false, error: httpFailure(path, ex.status, ex.statusText, ex.payload) }
  }
  return { ok: true, data: null, roundTripMs: ex.roundTripMs }
}

// ---------------------------------------------------------------------------
// Van fout naar vertaalsleutel
// ---------------------------------------------------------------------------

/**
 * Vertaalt een mislukking naar de **sleutel** van de melding die de gebruiker
 * ziet — nooit naar tekst; user-facing tekst hoort uitsluitend in de catalogi
 * van `packages/i18n` (§17.8). Zelfde opzet als `authErrorMessageKey`.
 *
 * `conflict` staat hier bewust ook in, maar de uploader gebruikt hem niet: daar
 * betekent een conflict "deze foto staat er al", en dat is informatie en geen
 * fout. Buiten de upload — bijvoorbeeld een tweede hoofdfoto zetten terwijl een
 * ander tabblad dat net deed — is het dat wél.
 */
export function mediaErrorMessageKey(failure: MediaFailure): string {
  switch (failure.code) {
    case 'validation_failed':
      return 'dashboard.media.error.rejected'
    case 'conflict':
      return 'dashboard.media.error.conflict'
    case 'not_found':
      return 'dashboard.media.error.notFound'
    case 'forbidden':
      return 'dashboard.media.error.forbidden'
    case 'rate_limited':
      return 'dashboard.media.error.rateLimited'
    default:
      // invalid_credentials, mfa_required, internal, en alles zonder code
      // (netwerkfout, onleesbaar antwoord): voor de gebruiker is dat één en
      // dezelfde situatie — het lukt nu niet.
      return 'dashboard.media.error.unavailable'
  }
}
